import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  TICKET_TYPES,
  TICKET_STATUSES,
  SEVERITIES,
  STATUS_COLORS,
  SEVERITY_COLORS,
  TYPE_COLORS,
} from '../../config/constants';
import pocketbaseService from '../../services/pocketbase';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const {
  FiEye,
  FiEdit3,
  FiCheckCircle,
  FiRotateCcw,
  FiCopy,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
} = FiIcons;

const TicketTable = ({ onTicketSelect, refreshKey }) => {
  const {
    selectedApp,
    statusFilters,
    setStatusFilters,
    searchQuery,
    showToast,
    openCreateTicketModal,
    inboxPreset,
    setInboxPreset,
  } = useApp();
  const { user } = useAuth();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dateRange, setDateRange] = useState('90');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [undo, setUndo] = useState(null);
  const [reopenTicket, setReopenTicket] = useState(null);
  const [reopening, setReopening] = useState(false);

  const dateFilters = () => {
    if (dateRange === 'all') return { dateRange: 'all' };
    if (dateRange === 'custom') {
      return {
        dateFrom: customFrom ? new Date(`${customFrom}T00:00:00`).toISOString() : undefined,
        dateTo: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : undefined,
      };
    }
    const date = new Date();
    date.setDate(date.getDate() - Number(dateRange || 90));
    return { dateFrom: date.toISOString() };
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await pocketbaseService.getTickets({
        app: selectedApp,
        statuses: statusFilters,
        search: searchQuery,
        type,
        severity,
        assignedTo,
        ...dateFilters(),
        page,
        perPage,
      });
      setTickets(response.items || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error(error);
      showToast('Failed to load tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedApp, statusFilters, searchQuery, type, severity, assignedTo, dateRange, customFrom, customTo, page, perPage, refreshKey]);

  useEffect(() => {
    if (!inboxPreset) return;
    if (inboxPreset.statuses) setStatusFilters(inboxPreset.statuses);
    if (inboxPreset.type !== undefined) setType(inboxPreset.type || '');
    if (inboxPreset.severity !== undefined) setSeverity(inboxPreset.severity || '');
    if (inboxPreset.unassigned) setAssignedTo('');
    if (inboxPreset.dateRange) setDateRange(inboxPreset.dateRange);
    setPage(1);
    setInboxPreset(null);
  }, [inboxPreset]);

  useEffect(() => {
    if (!reopenTicket) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !reopening) setReopenTicket(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reopenTicket, reopening]);

  const addLog = async (id, message) => {
    try {
      await pocketbaseService.createTicketLog({
        ticket: id,
        authorType: 'Agent',
        authorName: user?.name || user?.email || 'Agent',
        authorEmail: user?.email || '',
        message,
        isInternal: true,
      });
    } catch (error) {
      console.warn(error);
    }
  };

  const changeStatus = async (ticket, nextStatus) => {
    const previousStatus = ticket.status;
    try {
      await pocketbaseService.updateTicket(ticket.id, { status: nextStatus });
      setTickets((current) => current.map((item) => item.id === ticket.id ? { ...item, status: nextStatus } : item));
      addLog(ticket.id, nextStatus === 'Resolved' ? 'Ticket marked as Resolved' : 'Ticket reopened');
      showToast(nextStatus === 'Resolved' ? 'Ticket resolved' : 'Ticket reopened');
      if (nextStatus === 'Resolved') {
        setUndo({ id: ticket.id, status: previousStatus });
        window.setTimeout(() => setUndo(null), 5000);
      }
      return true;
    } catch {
      showToast('Failed to update ticket', 'error');
      return false;
    }
  };

  const confirmReopen = async () => {
    if (!reopenTicket || reopening) return;
    setReopening(true);
    const success = await changeStatus(reopenTicket, 'In Progress');
    setReopening(false);
    if (success) setReopenTicket(null);
  };

  const undoResolve = async () => {
    if (!undo) return;
    try {
      await pocketbaseService.updateTicket(undo.id, { status: undo.status });
      setTickets((current) => current.map((item) => item.id === undo.id ? { ...item, status: undo.status } : item));
      addLog(undo.id, 'Resolve action undone');
      setUndo(null);
      showToast('Resolved status undone');
    } catch {
      showToast('Undo failed', 'error');
    }
  };

  const copyReference = async (ticketRef) => {
    try {
      await navigator.clipboard.writeText(ticketRef);
      showToast('Ticket reference copied');
    } catch {
      showToast('Could not copy ticket reference', 'error');
    }
  };

  const iconButton = 'inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-surface text-muted hover:bg-primary-soft hover:text-primary transition-colors';
  const resolveButton = 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-border bg-accent-soft text-text hover:bg-accent font-semibold text-xs transition-colors whitespace-nowrap';
  const reopenButton = 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-primary bg-surface text-primary hover:bg-primary-soft font-semibold text-xs transition-colors whitespace-nowrap';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text">Inbox</h2>
          <p className="text-sm text-muted">Manage, preview and resolve support tickets.</p>
        </div>
        <button onClick={openCreateTicketModal} className="inline-flex min-h-11 items-center px-4 py-2 bg-primary text-surface rounded-xl hover:bg-primary-hover font-semibold shadow-sm">
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />New Ticket
        </button>
      </div>

      <div className="bg-surface border border-border rounded-2xl p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
          <Filter label="Type"><select value={type} onChange={(event) => { setType(event.target.value); setPage(1); }} className="control mt-1"><option value="">All Types</option>{TICKET_TYPES.map((value) => <option key={value}>{value}</option>)}</select></Filter>
          <Filter label="Severity"><select value={severity} onChange={(event) => { setSeverity(event.target.value); setPage(1); }} className="control mt-1"><option value="">All Severities</option>{SEVERITIES.map((value) => <option key={value}>{value}</option>)}</select></Filter>
          <Filter label="Assign To"><input value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)} placeholder="Agent name/email" className="control mt-1" /></Filter>
          <Filter label="Status"><select value={statusFilters.length === 1 ? statusFilters[0] : ''} onChange={(event) => setStatusFilters(event.target.value ? [event.target.value] : [])} className="control mt-1"><option value="">All Statuses</option>{TICKET_STATUSES.map((value) => <option key={value}>{value}</option>)}</select></Filter>
          <Filter label="Date Range"><select value={dateRange} onChange={(event) => setDateRange(event.target.value)} className="control mt-1"><option value="7">Last 7 Days</option><option value="30">Last 30 Days</option><option value="90">Last 90 Days</option><option value="all">All Time</option><option value="custom">Custom</option></select></Filter>
        </div>
        {dateRange === 'custom' && <div className="grid sm:grid-cols-2 gap-3 mt-3 max-w-xl"><input type="date" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} className="control" /><input type="date" value={customTo} onChange={(event) => setCustomTo(event.target.value)} className="control" /></div>}
      </div>

      {undo && <div className="flex items-center justify-between bg-accent-soft border border-border text-text rounded-xl px-4 py-3"><span className="text-sm font-medium">Ticket marked as resolved.</span><button onClick={undoResolve} className="font-semibold text-primary hover:underline">Undo</button></div>}

      <div className="ticket-table-desktop bg-surface rounded-2xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background"><tr>{['Reference', 'App', 'Type', 'Status', 'Severity', 'Assigned To', 'Requester', 'Updated', 'Actions'].map((heading) => <th key={heading} className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase whitespace-nowrap">{heading}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {!loading && tickets.map((ticket) => (
                <tr key={ticket.id} onClick={() => onTicketSelect(ticket, { tab: 'overview' })} className="hover:bg-primary-soft cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs text-primary">{ticket.ticketRef}</td>
                  <td className="px-4 py-3 text-sm">{ticket.expand?.app?.appName || '-'}</td>
                  <td className="px-4 py-3"><Badge classes={TYPE_COLORS[ticket.type]}>{ticket.type}</Badge></td>
                  <td className="px-4 py-3"><Badge classes={STATUS_COLORS[ticket.status]}>{ticket.status}</Badge></td>
                  <td className="px-4 py-3"><Badge classes={SEVERITY_COLORS[ticket.severity]}>{ticket.severity || '-'}</Badge></td>
                  <td className="px-4 py-3 text-sm">{ticket.assignedTo || '-'}</td>
                  <td className="px-4 py-3 text-sm">{ticket.requesterEmail || ticket.requesterName || '-'}</td>
                  <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">{formatUpdated(ticket.updated)}</td>
                  <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}><TicketActions ticket={ticket} iconButton={iconButton} resolveButton={resolveButton} reopenButton={reopenButton} onTicketSelect={onTicketSelect} changeStatus={changeStatus} requestReopen={setReopenTicket} copyReference={copyReference} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <Loading />}
        {!loading && !tickets.length && <Empty />}
        <Pagination page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
      </div>

      <div className="ticket-mobile-list">
        {loading && <div className="bg-surface border border-border rounded-2xl"><Loading /></div>}
        {!loading && !tickets.length && <div className="bg-surface border border-border rounded-2xl"><Empty /></div>}
        {!loading && tickets.map((ticket) => (
          <article key={ticket.id} className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
            <button type="button" onClick={() => onTicketSelect(ticket, { tab: 'overview' })} className="w-full text-left p-4 min-h-11">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><p className="font-mono text-xs font-semibold text-primary break-all">{ticket.ticketRef}</p><h3 className="font-bold text-text mt-1 line-clamp-2">{ticket.subject || 'Untitled ticket'}</h3><p className="text-xs text-muted mt-1 truncate">{ticket.expand?.app?.appName || 'Unknown App'}</p></div>
                <Badge classes={STATUS_COLORS[ticket.status]}>{ticket.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-3"><Badge classes={TYPE_COLORS[ticket.type]}>{ticket.type}</Badge><Badge classes={SEVERITY_COLORS[ticket.severity]}>{ticket.severity || 'None'}</Badge></div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                <div><p className="text-muted">Assigned To</p><p className="font-semibold text-text mt-0.5 truncate">{ticket.assignedTo || 'Unassigned'}</p></div>
                <div><p className="text-muted">Updated</p><p className="font-semibold text-text mt-0.5">{formatUpdated(ticket.updated)}</p></div>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-muted"><SafeIcon icon={FiUser} className="w-4 h-4 shrink-0" /><span className="truncate">{ticket.requesterEmail || ticket.requesterName || 'No requester'}</span></div>
            </button>
            <div className="border-t border-border bg-background p-3" onClick={(event) => event.stopPropagation()}>
              <TicketActions ticket={ticket} mobile iconButton={iconButton} resolveButton={resolveButton} reopenButton={reopenButton} onTicketSelect={onTicketSelect} changeStatus={changeStatus} requestReopen={setReopenTicket} copyReference={copyReference} />
            </div>
          </article>
        ))}
        {!loading && tickets.length > 0 && <div className="bg-surface border border-border rounded-2xl overflow-hidden"><Pagination page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} /></div>}
      </div>

      {reopenTicket && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !reopening) setReopenTicket(null);
          }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="reopen-ticket-title" className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary bg-primary-soft text-primary">
                  <SafeIcon icon={FiRotateCcw} className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 id="reopen-ticket-title" className="text-lg font-bold text-text">Reopen this ticket?</h3>
                  <p className="mt-1 text-sm leading-6 text-muted">This will move the ticket from Resolved to In Progress and record the action in the ticket log.</p>
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-border bg-background px-4 py-3">
                <p className="font-mono text-xs font-semibold text-primary break-all">{reopenTicket.ticketRef}</p>
                <p className="mt-1 text-sm font-semibold text-text line-clamp-2">{reopenTicket.subject || 'Untitled ticket'}</p>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  disabled={reopening}
                  onClick={() => setReopenTicket(null)}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text hover:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={reopening}
                  onClick={confirmReopen}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-surface hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <SafeIcon icon={FiRotateCcw} className="h-4 w-4" />
                  {reopening ? 'Reopening...' : 'Yes, Reopen Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TicketActions = ({ ticket, mobile = false, iconButton, resolveButton, reopenButton, onTicketSelect, changeStatus, requestReopen, copyReference }) => (
  <div className={`flex items-center ${mobile ? 'justify-between gap-2' : 'gap-1.5'}`}>
    <button className={`${iconButton} ${mobile ? 'min-w-11 min-h-11 flex-1' : ''}`} title="Preview ticket" aria-label="Preview ticket" onClick={() => onTicketSelect(ticket, { tab: 'overview' })}><SafeIcon icon={FiEye} className="w-4 h-4" /></button>
    <button className={`${iconButton} ${mobile ? 'min-w-11 min-h-11 flex-1' : ''}`} title="Edit ticket" aria-label="Edit ticket" onClick={() => onTicketSelect(ticket, { tab: 'overview', edit: true })}><SafeIcon icon={FiEdit3} className="w-4 h-4" /></button>
    {ticket.status === 'Resolved' ? (
      <button className={`${reopenButton} ${mobile ? 'min-h-11 flex-[1.6]' : ''}`} onClick={() => requestReopen(ticket)}><SafeIcon icon={FiRotateCcw} className="w-4 h-4" />Reopen</button>
    ) : ticket.status !== 'Closed' ? (
      <button className={`${resolveButton} ${mobile ? 'min-h-11 flex-[1.6]' : ''}`} onClick={() => changeStatus(ticket, 'Resolved')}><SafeIcon icon={FiCheckCircle} className="w-4 h-4" />Resolve</button>
    ) : null}
    <button className={`${iconButton} ${mobile ? 'min-w-11 min-h-11 flex-1' : ''}`} title="Copy ticket reference" aria-label="Copy ticket reference" onClick={() => copyReference(ticket.ticketRef)}><SafeIcon icon={FiCopy} className="w-4 h-4" /></button>
  </div>
);

const Pagination = ({ page, totalPages, perPage, setPage, setPerPage }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-background">
    <div className="flex items-center gap-2"><span className="text-sm text-muted">Rows</span><select value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setPage(1); }} className="border border-border bg-surface rounded-lg px-2 py-2 min-h-11"><option>25</option><option>50</option><option>100</option></select></div>
    <div className="flex items-center gap-2"><button disabled={page <= 1} onClick={() => setPage((current) => current - 1)} className="min-w-11 min-h-11 inline-flex items-center justify-center border border-border bg-surface rounded-lg disabled:opacity-40"><SafeIcon icon={FiChevronLeft} /></button><span className="text-sm whitespace-nowrap">{page} / {totalPages}</span><button disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)} className="min-w-11 min-h-11 inline-flex items-center justify-center border border-border bg-surface rounded-lg disabled:opacity-40"><SafeIcon icon={FiChevronRight} /></button></div>
  </div>
);

const Filter = ({ label, children }) => <label className="block min-w-0"><span className="text-xs font-semibold text-muted">{label}</span>{children}</label>;
const Badge = ({ classes = '', children }) => <span className={`inline-flex text-xs px-2 py-1 rounded-full border whitespace-nowrap ${classes}`}>{children}</span>;
const Loading = () => <div className="p-10 text-center text-muted">Loading tickets...</div>;
const Empty = () => <div className="p-10 text-center text-muted">No tickets found.</div>;
const formatUpdated = (value) => value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : '-';

export default TicketTable;